<script lang="ts" setup>
import isIos from 'is-ios'
import { AnnotationSetType, BarMitzvahType, SentenceType, TorahColumnOverlayType } from 'types/types'
import { computed, ref, shallowRef, watch, watchEffect } from 'vue'

import AnnotatedText from '@/components/AnnotatedText.vue'
import ColorationToolbar from '@/components/ColorationToolbar.vue'
import FontSizeControls from '@/components/FontSizeControls.vue'
import SentencePopover from '@/components/SentencePopover.vue'
import TikkunToggle from '@/components/TikkunToggle.vue'
import TransformationSelect from '@/components/TransformationSelect.vue'
import { useFont } from '@/composables/font'
import { StyledRecordings } from '@/composables/styledRecording'
import { useAnnotations } from '@/composables/useAnnotations'
import { usePopover } from '@/composables/usePopover'
import { useTextColoration } from '@/composables/useTextColoration'
import { useTransformation } from '@/composables/useTransformation'
import {
  COLUMN_BASE_WIDTH_EM,
  COLUMN_LINE_HEIGHT,
  OVERVIEW_TEXT_NAME,
  SENTENCE_ELEMENT_ID_SUFFIX,
  TOOLTIP_OPEN_DELAY_MS,
  VOICE_MODE_READING,
  VOICE_MODE_SINGING,
} from '@/constants/practiceConstants'
import SetTorahColumnTrim from '@/mutations/SetTorahColumnTrim.graphql'
import store from '@/store'
import { runReportedMutation } from '@/store/mutationHelpers'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import { parseAnnotationStyle } from '@/util/annotationStyle'
import { SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import { normalizeSentenceText } from '@/util/CharacterClasses'
import { adjustedTrim, resolveColumnTrim } from '@/util/columnTrim'
import { hasRecordingError, isProcessing, isRead, isSung } from '@/util/RecordingHelpers'
import { escapeCssString, escapeHtml } from '@/util/util'

// {{{ types
type RecordingStatus = 'none' | 'reading' | 'singing' | 'complete' | 'processing' | 'error'

/** Background colors for recording-status sentence highlighting and legend swatches. */
const RECORDING_STATUS_BACKGROUNDS: Record<Exclude<RecordingStatus, 'none'>, string> & Record<'none', null> = {
  complete: '#e8f5e9',
  error: '#ffebee',
  none: null,
  processing: '#f5f5f5',
  reading: '#e3f2fd',
  singing: '#fff8e1',
}

// }}}
// {{{ props
interface Props {
  sentences: SentenceType[]
  annotatedSentences: AnnotatedSentence[]
  sentenceRecordingMetadatas: StyledRecordings
  barMitzvah: BarMitzvahType | null
  columnOverlay: TorahColumnOverlayType | null
  // True right after an upload+analyze, to auto-enter column edit mode once rendered.
  autoEdit?: boolean
  isAliyah: boolean
  isColumnMode: boolean
  userIsLoggedIn: boolean
  downloadingSentenceRecordingId: string | null
  playingSentenceRecordingId: string | null
  playing: boolean
  paused: boolean
  printTitle: { ltr: string; rtl: string; studentName: string }
}

const props = defineProps<Props>()
// }}}

// {{{ emits
interface Emits {
  (e: 'editRecording', item: SentenceRecordingMetadataWithPitchedAudios, annotatedSentence: AnnotatedSentence, sentenceIndex: number): void
  (e: 'deleteRecording', item: SentenceRecordingMetadataWithPitchedAudios): void
  (e: 'downloadRecording', item: SentenceRecordingMetadataWithPitchedAudios): void
  (e: 'playRecording', item: SentenceRecordingMetadataWithPitchedAudios): void
  (e: 'pauseRecording'): void
  (e: 'resumeRecording'): void
  (e: 'stopRecording'): void
  (e: 'quickRecord', annotatedSentence: AnnotatedSentence, sentenceIndex: number, mode: 'reading' | 'singing'): void
  (e: 'handleSentenceClick', annotatedSentence: AnnotatedSentence, sentenceIndex: number): void
  (e: 'updateColumnTrim', skipPrefixWords: number, cutTrailingWords: number): void
  (e: 'autoEditConsumed'): void
  (e: 'extendColumnRange', edge: 'start' | 'end'): void
}

const emit = defineEmits<Emits>()
// }}}

// {{{ font size
const { font, fontSize } = useFont()
const localFontSize = ref(fontSize.value)
// }}}

// {{{ transformation + tikkun
const isColumnMode = computed(() => props.isColumnMode)
const { tikkunStyle, transformation } = useTransformation({ columnMode: isColumnMode })
// Both aliyah mode and column mode display Torah content — share Torah font + tikkun.
const isTorahContent = computed(() => props.isAliyah || props.isColumnMode)
// }}}

// {{{ newlines + print
const forceNewlines = ref(false)

watchEffect(() => {
  if (tikkunStyle.value) {
    forceNewlines.value = false
  }
})
const printContentRef = ref<HTMLElement | null>(null)

function exportPdf() {
  const el = printContentRef.value
  if (!el) {
    return
  }

  const cloned = el.cloneNode(true) as HTMLElement

  // Strip recording-status inline styles — only teacher-applied colors belong in the PDF
  for (const span of cloned.querySelectorAll(`[id$="${SENTENCE_ELEMENT_ID_SUFFIX}"]`)) {
    ;(span as HTMLElement).style.removeProperty('background-color')
    ;(span as HTMLElement).style.removeProperty('border-radius')
    ;(span as HTMLElement).style.removeProperty('padding')
    ;(span as HTMLElement).style.removeProperty('animation')
  }

  // Collect all @font-face rules from loaded stylesheets
  const fontRules: string[] = []
  for (const sheet of document.styleSheets) {
    try {
      for (const rule of sheet.cssRules) {
        if (rule instanceof CSSFontFaceRule) {
          fontRules.push(rule.cssText)
        }
      }
    } catch {
      // Cross-origin stylesheets throw — skip them
    }
  }

  // Capture the live column's computed width and font-size so the print CSS can
  // reproduce the exact same layout. The text spans use font-size as a percentage,
  // which is relative to the parent — without Vuetify in the print window the
  // inherited base font-size differs, causing lines to overflow.
  const columnContainer = el.querySelector<HTMLElement>('.column-justify')
  const columnWidthPx = columnContainer ? Math.ceil(columnContainer.getBoundingClientRect().width) : 0
  const columnFontSize = columnContainer ? getComputedStyle(columnContainer).fontSize : ''

  const printWindow = window.open('', '_blank')
  if (!printWindow) {
    return
  }

  printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>${fontRules.join('\n')}

.torah { font-family: StamAshkenazCLM, sans-serif; }
.david { font-family: TaameyDavidCLM-Medium, sans-serif; }
.frank { font-family: TaameyFrankCLM-Medium, sans-serif; }
.ashkenaz { font-family: TaameyAshkenaz-Medium, sans-serif; }
.cardo { font-family: Cardo-regular104s, sans-serif; }
.noto { font-family: NotoSerifHebrew-Regular, sans-serif; }
.shlomo { font-family: Shlomo, sans-serif; }
.made { font-family: MADE-Tommy, sans-serif; }

.title {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  font-size: 1.8em;
  font-weight: 600;
  margin: 0 0 0.8em;
  padding-bottom: 0.4em;
  border-bottom: 2px solid #ccc;
}
.show-newline-glyph::after { display: none; }

/* Column mode styles — needed for Torah column print layout */
.column-justify {
  text-align: justify;
  text-align-last: justify;
  text-justify: inter-word;${columnWidthPx ? `\n  max-width: ${columnWidthPx}px !important;\n  margin-inline: auto !important;` : ''}${columnFontSize ? `\n  font-size: ${columnFontSize} !important;` : ''}
}
.stretch-point {
  display: inline-block;
  width: 0;
  overflow: hidden;
  color: transparent;
  vertical-align: baseline;
}
.column-line-filler {
  display: inline-block;
  width: 0;
  overflow: hidden;
  vertical-align: baseline;
}
.column-sentence-gap {
  background: transparent;
}
${
  forceNewlines.value
    ? `span:has(> [id$="${SENTENCE_ELEMENT_ID_SUFFIX}"]) { display: block; break-inside: avoid; }
span:has(> [id$="${SENTENCE_ELEMENT_ID_SUFFIX}"]) > br { display: none; }`
    : ''
}
body { margin: 24px; }
@page {
  size: ${props.isColumnMode ? 'portrait' : 'landscape'};
  margin: 1.5cm 1cm 0.8cm 1cm;
  @top-left { content: ""; }
  @top-center { content: ""; }
  @top-right { content: ""; }
  @bottom-left {
    content: "${escapeCssString(props.printTitle.studentName)}";
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    font-size: 10pt;
    color: #666;
  }
  @bottom-center { content: ""; }
  @bottom-right {
    content: counter(page) " / " counter(pages);
    font-size: 10pt;
    color: #666;
  }
}
@media print { body { margin: 0; padding: 0; } }
  </style>
</head>
<body dir="ltr"><div class="title"><span>${escapeHtml(props.printTitle.ltr)}</span><span dir="rtl">${escapeHtml(props.printTitle.rtl)}</span></div>${cloned.outerHTML}</body>
</html>`)
  // Attach handlers BEFORE document.close(), which can fire `load` synchronously
  // for a simple popup and leave a post-close listener unregistered.
  printWindow.onload = () => {
    printWindow.focus()
    const doPrint = () => {
      // Close only after the print dialog is dismissed — closing right after
      // print() kills the window before the dialog can render in some browsers.
      printWindow.onafterprint = () => printWindow.close()
      printWindow.print()
    }
    if (printWindow.document.fonts?.ready) {
      printWindow.document.fonts.ready.then(doPrint)
    } else {
      doPrint()
    }
  }
  printWindow.document.close()
}
// }}}

// {{{ annotations
const overlayId = computed(() => props.columnOverlay?.id ?? null)
const { annotations } = useAnnotations(
  computed(() => props.barMitzvah),
  overlayId,
)

// DEBUG: log annotation availability on mount and when annotations change
console.log(
  'FullTextOverview MOUNT: isColumnMode=',
  props.isColumnMode,
  'sentences=',
  props.sentences.length,
  'annotations=',
  annotations.value.length,
)
watch(
  annotations,
  (val) => {
    const newlineCount = val.reduce(
      (sum, s) => sum + (s.annotations?.filter((a) => parseAnnotationStyle(a.style).newlineAfter).length ?? 0),
      0,
    )
    console.log('FullTextOverview annotations changed:', val.length, 'sets,', newlineCount, 'newlineAfter annotations')
  },
  { immediate: true },
)
// }}}

// {{{ column mode sentence/annotation trimming
// In column mode, trim the column to match the visible text on the photo.
// skipPrefixWords / cutTrailingWords are word counts hidden from the start / end of
// the analyzed verse range; they cascade across verses (see resolveColumnTrim), so
// a trim larger than the first/last verse hides whole verses and a partial one.
const columnWordCounts = computed(() => props.sentences.map((s) => (s.text ? normalizeSentenceText(s.text).split(' ').length : 0)))
const columnTotalWords = computed(() => columnWordCounts.value.reduce((sum, c) => sum + c, 0))

const columnTrimSpan = computed(() => {
  if (!props.isColumnMode || !props.columnOverlay || !props.sentences.length) {
    return null
  }
  const skip = props.columnOverlay.skipPrefixWords ?? 0
  const cut = props.columnOverlay.cutTrailingWords ?? 0
  if (!skip && !cut) {
    return null
  }
  return resolveColumnTrim(columnWordCounts.value, skip, cut)
})

function trimSentenceWords(text: string, sliceStart: number, sliceEnd?: number): string {
  return normalizeSentenceText(text).split(' ').slice(sliceStart, sliceEnd).join(' ')
}

const columnTrimmedSentences = computed<SentenceType[]>(() => {
  const span = columnTrimSpan.value
  if (!span) {
    return props.sentences
  }
  const result: SentenceType[] = []
  for (let i = span.firstIdx; i <= span.lastIdx; i++) {
    const sentence = props.sentences[i]
    if (!sentence) {
      continue
    }
    const lead = i === span.firstIdx ? span.firstLeadTrim : 0
    const tail = i === span.lastIdx ? span.lastTailTrim : 0
    if ((lead || tail) && sentence.text) {
      result.push({ ...sentence, text: trimSentenceWords(sentence.text, lead, tail > 0 ? -tail : undefined) })
    } else {
      result.push(sentence)
    }
  }
  return result
})

// When the first/last visible sentence is trimmed, annotation character indices
// (relative to the original normalized text) must be shifted to align with the
// shorter text. Verses fully hidden by the cascade aren't in columnTrimmedSentences,
// so the renderer ignores their annotation sets — we only adjust the partial edges.
const columnTrimmedAnnotations = computed<AnnotationSetType[]>(() => {
  const span = columnTrimSpan.value
  if (!span) {
    return annotations.value
  }
  const firstId = props.sentences[span.firstIdx]?.id
  const lastId = props.sentences[span.lastIdx]?.id

  return annotations.value.map((annotationSet) => {
    const sid = annotationSet.sentence.id
    const isFirst = sid === firstId && span.firstLeadTrim > 0
    const isLast = sid === lastId && span.lastTailTrim > 0
    if (!isFirst && !isLast) {
      return annotationSet
    }

    const origText = props.sentences.find((s) => s.id === sid)?.text ?? ''
    const origNorm = normalizeSentenceText(origText)

    // Prefix offset: how many normalized characters were removed from the start.
    let charOffset = 0
    if (isFirst) {
      charOffset = origNorm.length - normalizeSentenceText(trimSentenceWords(origText, span.firstLeadTrim)).length
    }

    // Max index: drop annotations beyond the visible suffix.
    let maxCharIndex = Infinity
    if (isLast) {
      const lead = isFirst ? span.firstLeadTrim : 0
      maxCharIndex = charOffset + normalizeSentenceText(trimSentenceWords(origText, lead, -span.lastTailTrim)).length
    }

    const adjusted = annotationSet.annotations
      .filter((a) => a.index >= charOffset && a.index < maxCharIndex)
      .map((a) => ({ ...a, index: a.index - charOffset }))

    return { ...annotationSet, annotations: adjusted }
  })
})
// Dynamic column width scales with font size.
// At 100% font, ~42 chars/line × ~0.6em/char ≈ 25em. Add 25% margin → 32em.
// Font size is applied as a percentage on the inner spans, so we scale the em value.
const columnTextStyle = computed(() => {
  if (!props.isColumnMode) {
    return {}
  }
  const scale = localFontSize.value / 100
  return { margin: '0 auto', maxWidth: `${COLUMN_BASE_WIDTH_EM * scale}em` }
})
// }}}

// }}}

// {{{ coloration
// Compute charOffset per sentence for column trimming — only the first visible
// sentence can have a non-zero offset (when the lead trim removes leading words).
const sentenceCharOffsets = computed(() => {
  const map = new Map<string, number>()
  const span = columnTrimSpan.value
  if (!span || span.firstLeadTrim <= 0) {
    return map
  }
  const first = props.sentences[span.firstIdx]
  if (!first?.text) {
    return map
  }
  const origNorm = normalizeSentenceText(first.text)
  const trimmedNorm = normalizeSentenceText(trimSentenceWords(first.text, span.firstLeadTrim))
  const offset = origNorm.length - trimmedNorm.length
  if (offset > 0) {
    map.set(first.id, offset)
  }
  return map
})

const coloration = useTextColoration({
  barMitzvah: computed(() => props.barMitzvah),
  disabled: computed(() => false),
  overlayId,
  sentenceCharOffsets,
  sentenceIds: computed(() => (props.sentences.length ? props.sentences.map((s) => s.id) : null)),
})

const effectiveClickableSentence = computed(() => (coloration.active.value ? null : clickableSentence))
const effectiveClickableChar = computed(() => (coloration.active.value && !coloration.newlineBrushActive.value ? () => true : null))
const effectiveDraggableChar = computed(() => (coloration.active.value && !coloration.newlineBrushActive.value ? () => true : null))
const effectiveClickableWord = computed(() => (coloration.newlineBrushActive.value ? () => true : null))

// Capture annotatedSentences from AnnotatedText for auto-coloring
const overviewAnnotatedSentences = shallowRef<AnnotatedSentence[]>([])

function handleAutoColor() {
  const userProps = store.getters.user.user?.properties
  coloration.autoColorVowels(overviewAnnotatedSentences.value, {
    kamatzKatanColor: userProps?.kamatzKatanColor ?? undefined,
    shewaNaColor: userProps?.shewaNaColor ?? undefined,
  })
}

// {{{ column edge trim editor
// At least one word of the whole column must stay visible, so skip + cut can hide
// at most (total words - 1). The trim cascades across verses (resolveColumnTrim).

// Reveal a word (delta -1) or hide one (delta +1) at the start or end of the
// column, persisting the new trim on the overlay and updating the displayed text.
async function onAdjustTrim(edge: 'start' | 'end', delta: number) {
  if (!props.columnOverlay) {
    return
  }
  const curSkip = props.columnOverlay.skipPrefixWords ?? 0
  const curCut = props.columnOverlay.cutTrailingWords ?? 0
  // Revealing (delta < 0) when nothing is hidden at this edge means the teacher wants
  // a word the OCR didn't include — pull in the adjacent verse from outside the range.
  if (delta < 0 && ((edge === 'start' && curSkip === 0) || (edge === 'end' && curCut === 0))) {
    console.log('FullTextOverview onAdjustTrim: extend beyond range at', edge)
    emit('extendColumnRange', edge)
    return
  }
  const maxRemovable = Math.max(0, columnTotalWords.value - 1)
  const skip = edge === 'start' ? adjustedTrim(curSkip, delta, maxRemovable - curCut) : curSkip
  const cut = edge === 'end' ? adjustedTrim(curCut, delta, maxRemovable - curSkip) : curCut
  if (skip === curSkip && cut === curCut) {
    return
  }
  const payload = await runReportedMutation(
    SetTorahColumnTrim,
    { cutTrailingWords: cut, id: props.columnOverlay.id, skipPrefixWords: skip },
    'setTorahColumnTrim',
    store.dispatch.snackbar.showError,
  )
  if (payload) {
    emit('updateColumnTrim', payload.skipPrefixWords ?? skip, payload.cutTrailingWords ?? cut)
  }
}

// After a fresh upload+analyze, drop the teacher into column edit mode (Style on,
// Column tab) once the column has actually rendered, then clear the request.
// immediate: true is required — this component is v-if'd out until sentences load,
// so by the time it mounts the condition is often already true (no change to react to).
watch(
  () => !!props.autoEdit && props.isColumnMode && props.sentences.length > 0,
  (ready) => {
    console.log(
      'FullTextOverview auto-edit watcher: ready =',
      ready,
      '(autoEdit=',
      props.autoEdit,
      'columnMode=',
      props.isColumnMode,
      'sentences=',
      props.sentences.length,
      ')',
    )
    if (!ready) {
      return
    }
    if (!coloration.active.value) {
      console.log('FullTextOverview auto-edit: activating coloration')
      coloration.toggleColoration()
    }
    if (!coloration.newlineBrushActive.value) {
      console.log('FullTextOverview auto-edit: activating Column (newline) brush')
      coloration.toggleNewlineBrush()
    }
    emit('autoEditConsumed')
  },
  { immediate: true },
)
// }}}

// {{{ popover state
interface OverviewPopoverData {
  anchorId: string
  sentenceIndex: number
}

const {
  close: closePopover,
  data: popoverData,
  toggle: togglePopover,
  visible: popoverVisible,
} = usePopover<OverviewPopoverData>({
  onBeforeClose: () => emit('stopRecording'),
})

// Close popover when entering coloration mode
watch(coloration.active, (active) => {
  if (active && popoverVisible.value) {
    closePopover()
  }
})

function openPopover(_annotatedSentence: AnnotatedSentence, sentenceIndex: number) {
  togglePopover(
    { anchorId: `${OVERVIEW_TEXT_NAME}:${sentenceIndex}${SENTENCE_ELEMENT_ID_SUFFIX}`, sentenceIndex },
    (current) => current.sentenceIndex === sentenceIndex,
  )
}
// }}}

// {{{ recording status per sentence
function getRecordingsForSentence(sentenceId: string): SentenceRecordingMetadataWithPitchedAudios[] {
  return props.sentenceRecordingMetadatas[sentenceId] ?? []
}

function recordingStatusForSentence(sentenceId: string): RecordingStatus {
  const recordings = getRecordingsForSentence(sentenceId)
  if (!recordings.length) {
    return 'none'
  }

  const anyError = recordings.some(hasRecordingError)
  const anyProcessing = recordings.some(isProcessing)

  if (anyError) {
    return 'error'
  }

  if (anyProcessing) {
    return 'processing'
  }

  const hasReading = recordings.some(isRead)
  const hasSinging = recordings.some(isSung)

  if (hasReading && hasSinging) {
    return 'complete'
  }
  if (hasReading) {
    return VOICE_MODE_READING
  }
  if (hasSinging) {
    return VOICE_MODE_SINGING
  }
  return 'none'
}

const sentenceStatuses = computed<Record<string, RecordingStatus>>(() => {
  const statuses: Record<string, RecordingStatus> = {}
  for (const sentence of props.sentences) {
    statuses[sentence.id] = recordingStatusForSentence(sentence.id)
  }
  return statuses
})

// }}}

// {{{ sentenceInlineStyle callback for AnnotatedText
function sentenceInlineStyle(annotatedSentence: AnnotatedSentence, _sentenceIndex: number): Record<string, string> | undefined {
  if (coloration.active.value) {
    return undefined
  }
  const status = sentenceStatuses.value[annotatedSentence.sentence().id] ?? 'none'
  const bg = RECORDING_STATUS_BACKGROUNDS[status]
  if (!bg) {
    return undefined
  }
  const style: Record<string, string> = { 'background-color': bg, 'border-radius': '4px', padding: '2px 0' }
  if (status === 'processing') {
    style.animation = 'pulse-bg 1.5s ease-in-out infinite'
  }
  return style
}
// }}}

// {{{ sentence clickability
function clickableSentence() {
  return true
}

function handleSentenceClick(annotatedSentence: AnnotatedSentence, sentenceIndex: number) {
  if (coloration.active.value) {
    return
  }
  openPopover(annotatedSentence, sentenceIndex)
}
// }}}

// {{{ popover data for current sentence
const popoverRecordings = computed<SentenceRecordingMetadataWithPitchedAudios[]>(() => {
  if (!popoverData.value) {
    return []
  }
  const sentence = props.sentences[popoverData.value.sentenceIndex]
  if (!sentence) {
    return []
  }
  return getRecordingsForSentence(sentence.id)
})

const popoverSentence = computed(() => {
  if (!popoverData.value) {
    return null
  }
  return props.sentences[popoverData.value.sentenceIndex] ?? null
})

const popoverAnnotatedSentence = computed(() => {
  if (!popoverData.value) {
    return null
  }
  return props.annotatedSentences[popoverData.value.sentenceIndex] ?? null
})
// }}}

// {{{ popover event handlers
function handlePopoverPlay(metadata: SentenceRecordingMetadataWithPitchedAudios) {
  emit('playRecording', metadata)
}

function handlePopoverPause() {
  emit('pauseRecording')
}

function handlePopoverResume() {
  emit('resumeRecording')
}

function handlePopoverEdit(metadata: SentenceRecordingMetadataWithPitchedAudios) {
  if (!popoverData.value || !popoverAnnotatedSentence.value) {
    return
  }
  const { sentenceIndex } = popoverData.value
  closePopover()
  emit('editRecording', metadata, popoverAnnotatedSentence.value, sentenceIndex)
}

function handlePopoverDelete(metadata: SentenceRecordingMetadataWithPitchedAudios) {
  emit('deleteRecording', metadata)
}

function handlePopoverDownload(metadata: SentenceRecordingMetadataWithPitchedAudios) {
  emit('downloadRecording', metadata)
}

function handleQuickRecord(mode: 'reading' | 'singing') {
  if (!popoverData.value || !popoverAnnotatedSentence.value) {
    return
  }
  const { sentenceIndex } = popoverData.value
  const annotatedSentence = popoverAnnotatedSentence.value
  closePopover()
  emit('quickRecord', annotatedSentence, sentenceIndex, mode)
}

// }}}

// {{{ close popover on sentence change
watch(
  () => props.sentences,
  () => {
    if (popoverVisible.value) {
      closePopover()
    }
  },
)
// }}}
</script>

<template>
  <div class="full-text-overview mt-4" @click.self="coloration.handleClickOutside">
    <!-- Toolbar: style, transformation, font size, newlines, print, legend -->
    <div class="toolbar-stack">
      <div class="overview-toolbar d-flex align-center ga-4">
        <v-tooltip
          v-if="!isIos"
          bottom
          :open-delay="TOOLTIP_OPEN_DELAY_MS"
          :text="
            barMitzvah
              ? props.isColumnMode
                ? 'Edit the column: line breaks, gaps, and start/end words'
                : 'Edit the text: colors, sizes, formatting, and line breaks'
              : 'Editing only works after you select a student'
          "
        >
          <template #activator="{ props: tipProps }">
            <span v-bind="tipProps">
              <v-btn
                :active="coloration.active.value"
                class="colorize-btn"
                :disabled="!barMitzvah"
                :variant="coloration.active.value ? 'flat' : 'outlined'"
                @click.stop="coloration.toggleColoration"
              >
                <v-icon left size="small">mdi-pencil</v-icon>
                Edit
              </v-btn>
            </span>
          </template>
        </v-tooltip>
        <transformation-select
          :include-torah-font="isTorahContent"
          :model-value="transformation"
          @update:model-value="transformation = $event"
        />
        <tikkun-toggle v-if="isTorahContent" v-model="tikkunStyle" :disabled="props.isColumnMode" />

        <font-size-controls v-model="localFontSize" :reset-value="fontSize" />

        <v-tooltip bottom :open-delay="TOOLTIP_OPEN_DELAY_MS" text="Toggle newlines between sentences">
          <template #activator="{ props: tipProps }">
            <v-btn
              v-bind="tipProps"
              :active="forceNewlines"
              density="comfortable"
              :disabled="props.isColumnMode"
              icon
              :variant="forceNewlines ? 'flat' : 'outlined'"
              @click="forceNewlines = !forceNewlines"
            >
              <v-icon size="small">mdi-keyboard-return</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

        <v-tooltip bottom :open-delay="TOOLTIP_OPEN_DELAY_MS" text="Export text as PDF">
          <template #activator="{ props: tipProps }">
            <v-btn v-bind="tipProps" density="comfortable" icon variant="outlined" @click="exportPdf">
              <v-icon size="small">mdi-printer</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

        <v-spacer />

        <!-- Color legend -->
        <div class="status-legend">
          <span class="legend-item"><span class="legend-swatch" /> No recordings</span>
          <span class="legend-item"
            ><span class="legend-swatch" :style="{ 'background-color': RECORDING_STATUS_BACKGROUNDS.reading }" /> Reading</span
          >
          <span class="legend-item"
            ><span class="legend-swatch" :style="{ 'background-color': RECORDING_STATUS_BACKGROUNDS.singing }" /> Singing</span
          >
          <span class="legend-item"
            ><span class="legend-swatch" :style="{ 'background-color': RECORDING_STATUS_BACKGROUNDS.complete }" /> Both</span
          >
          <span class="legend-item"
            ><span class="legend-swatch" :style="{ 'background-color': RECORDING_STATUS_BACKGROUNDS.error }" /> Error</span
          >
        </div>
      </div>

      <!-- Coloration Toolbar -->
      <coloration-toolbar
        :active="coloration.active.value"
        :background-color="coloration.backgroundColor.value"
        :bar-mitzvah="barMitzvah"
        :brush-kind="coloration.brushKind.value"
        :cut-trailing-words="props.columnOverlay?.cutTrailingWords ?? 0"
        :font-size="coloration.fontSize.value"
        :foreground-color="coloration.foregroundColor.value"
        :has-selection="coloration.hasSelection.value"
        :is-column-mode="isColumnMode"
        :newline-brush-active="coloration.newlineBrushActive.value"
        :skip-prefix-words="props.columnOverlay?.skipPrefixWords ?? 0"
        :total-words="columnTotalWords"
        :underline="coloration.underline.value"
        @adjust-trim="onAdjustTrim"
        @auto-color="handleAutoColor"
        @clear-all-colors="coloration.clearAllColors"
        @clear-all-whitespace="coloration.clearAllWhitespace"
        @clear-selection="coloration.clearSelectedAnnotations"
        @click.stop
        @close="coloration.toggleColoration"
        @set-brush-kind="coloration.setBrushKind"
        @toggle-newline-brush="coloration.toggleNewlineBrush"
        @update-background="(c) => coloration.applyColor('backgroundColor', c)"
        @update-font-size="(s) => coloration.applyColor('fontSize', s)"
        @update-foreground="(c) => coloration.applyColor('color', c)"
        @update-underline="coloration.applyUnderline"
      />
    </div>

    <v-card :ripple="false" @click="coloration.handleClickOutside">
      <v-card-text class="px-8 py-6" @click.self="coloration.handleClickOutside">
        <div ref="printContentRef" :style="columnTextStyle">
          <annotated-text
            v-if="userIsLoggedIn"
            :annotations="columnTrimmedAnnotations"
            :break-before-line="() => !props.isColumnMode && forceNewlines"
            :clickable-char="effectiveClickableChar"
            :clickable-sentence="effectiveClickableSentence"
            :clickable-word="effectiveClickableWord"
            :column-mode="props.isColumnMode"
            :default-font="font"
            :draggable-char="effectiveDraggableChar"
            :font-size="localFontSize"
            :is-char-selected="coloration.active.value ? coloration.isCharSelected.value : null"
            :is-segment-boundary-selected="coloration.active.value ? coloration.isSegmentBoundarySelected.value : null"
            :is-word-boundary-selected="coloration.active.value ? coloration.isWordBoundarySelected.value : null"
            :line-height="props.isColumnMode ? COLUMN_LINE_HEIGHT : undefined"
            :name="OVERVIEW_TEXT_NAME"
            :sentence-inline-style="sentenceInlineStyle"
            :sentences="columnTrimmedSentences"
            :show-end-of-sentence-character="!tikkunStyle"
            :show-newline-indicators="coloration.active.value"
            :show-sentence-numbers="!tikkunStyle && !props.isColumnMode"
            :transformation="transformation"
            @click-sentence="handleSentenceClick"
            @click-word="coloration.handleBrushClick"
            @dblclick-char="coloration.handleCharDblclick"
            @mousedown-char="coloration.handleCharMousedown"
            @mouseenter-char="coloration.handleCharMouseenter"
            @mouseup-text="coloration.handleMouseup"
            @update:annotated-sentences="overviewAnnotatedSentences = $event"
          />
        </div>
      </v-card-text>
    </v-card>

    <!-- Sentence Popover -->
    <sentence-popover
      v-if="popoverVisible && popoverSentence && popoverData"
      :anchor-id="popoverData?.anchorId"
      :downloading-sentence-recording-id="downloadingSentenceRecordingId"
      :paused="paused"
      :playing="playing"
      :playing-sentence-recording-id="playingSentenceRecordingId"
      :recordings="popoverRecordings"
      :sentence="popoverSentence"
      :sentence-index="popoverData.sentenceIndex"
      :visible="popoverVisible"
      @close="closePopover"
      @delete="handlePopoverDelete"
      @download="handlePopoverDownload"
      @edit="handlePopoverEdit"
      @pause="handlePopoverPause"
      @play="handlePopoverPlay"
      @quick-record="handleQuickRecord"
      @resume="handlePopoverResume"
    />
  </div>
</template>

<style scoped>
.toolbar-stack {
  position: sticky;
  top: var(--app-bar-height);
  z-index: 10;
  background: rgb(var(--v-theme-surface));
}

.overview-toolbar {
  padding: 8px 16px;
}

/* Status legend */
.status-legend {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.legend-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
}
</style>

<!-- Non-scoped: keyframes must be global for inline style animation references -->
<style>
@keyframes pulse-bg {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
